import { Select } from '@chakra-ui/react';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useGetUserMembershipTypes } from '../hooks/useGetUserMembershipTypes';

interface UserMembershipTypePickerProps {
  organizationId: string;
  value: string;
  onChange: (value: string) => void;
}

export default function UserMembershipTypePicker({
  organizationId,
  value,
  onChange,
}: UserMembershipTypePickerProps) {
  const { data: membershipTypes, isInitialLoading, isError } =
    useGetUserMembershipTypes(organizationId);
  const readState = classifyRead(isError, isInitialLoading, membershipTypes);

  if (readState === 'loading') {
    return <Select placeholder="Loading..." isDisabled />;
  }

  /**
   * Nobody asked. `organizationId` comes from `selectedOrganization?.id` two
   * components up (`AddUserMembershipModal`), so it is `undefined` for an
   * operator with nothing selected — and the hook is gated
   * `enabled: Boolean(organizationId)`, which React Query v4 reports as
   * `isLoading: true` with `fetchStatus: 'idle'`. The branch above therefore
   * used to render a disabled Select reading "Loading..." for as long as the
   * dialog stayed open: a control that promises a list it never asked for,
   * in the middle of assigning somebody a membership.
   *
   * The placeholder is the BORROWED scope key (R96-7 — `src/i18n/{en,fr}.ts`
   * are frozen behind PR #577), shared with `ScopePrompt` through
   * `scopePromptI18nKey` so the two cannot drift. A full `ScopePrompt` panel
   * is wrong here: this is one control inside a form, and the thing that fixes
   * it is the organization picker in the navbar, not anything in this dialog.
   */
  if (readState === 'never-issued') {
    return <Select placeholder={translate(scopePromptI18nKey('organization') as any)} isDisabled />;
  }

  /**
   * Before this branch existed, the picker already distinguished "in flight"
   * from "settled" and then folded "refused" into "settled and empty": a
   * failed read rendered a Select holding nothing but its own placeholder,
   * i.e. "this organization has no membership types". Whoever is looking at
   * it is mid-task — assigning a membership to a user — and the chooser they
   * need is the thing that silently emptied.
   */
  if (readState === 'unreadable') {
    return <InlineErrorState message={translate('userMembership.error_loading')} />;
  }

  return (
    <Select
      placeholder={translate('userMembership.select_membership_type')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {membershipTypes?.map((type) => (
        <option key={type.id} value={type.id}>
          {type.name}
        </option>
      ))}
    </Select>
  );
}